import { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import { SpacingType } from '@vds/core';
import { Icon, IconName } from '@components/icon';
import { Body } from '@components/text';
import './Banner.scss';

export type closeReason = 'timeout' | 'clickClose' | 'escapeKeyDown';
export interface BannerProps {
  /** Text to display in the body of the banner */
  body?: string;
  /** Additional content to display in the body of the banner */
  children?: React.ReactNode;
  /** Aria-label for dismiss (X) icon button */
  dismissLabel?: string;
  /** Show/hide the dismiss (X) icon button. default true (show) */
  isDismissable?: boolean;
  /** Should the banner have padding that matches Nav. default false */
  hasNavContainer?: boolean;
  /** Should the banner have a left icon. default true */
  hasStartIcon?: boolean;
  /** Optional icon override */
  icon?: IconName;
  /** Function to call when the banner is dismissed */
  onClose?: (event: React.SyntheticEvent | Event, reason: closeReason) => void;
  /** Function to call when the banner is focused */
  onFocus?: (event: React.FocusEvent) => void;
  /** Function to call when the banner is blurred */
  onBlur?: (event: React.FocusEvent) => void;
  /** Function to call when the banner is hovered */
  onMouseEnter?: (event: React.MouseEvent) => void;
  /** Function to call when the banner is unhovered */
  onMouseLeave?: (event: React.MouseEvent) => void;
  /** Styled variants of the banner. default is 'info' */
  variant?: 'info' | 'critical' | 'success';
  /** Transition effect for the banner. default is 'fade' */
  transitionName?: 'fade' | 'slide' | 'grow' | 'none';
  /** Duration of the transition effect in seconds. default is 0.3 */
  transitionDuration?: number;
  /** Duration to auto hide the banner in milliseconds. default is null */
  autoHideDuration?: number;
  /** Vertical offset for the banner. */
  verticalOffset?: SpacingType;
  /** Horizontal offset for the banner. */
  horizontalOffset?: SpacingType;
  /** Position of the banner. default is 'top-center' */
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  /** Show/hide the banner. default true */
  isOpen?: boolean;
  /** Additional classnames of the banner */
  className?: string;
  /** Banner has sticky behavior. default false */
  isSticky?: boolean;
}

/**
 * Banner component
 * @param body - text to display in the body of the banner
 * @param children - additional content to display in the body of the banner
 * @param dismissLabel - aria-label for dismiss (X) icon button
 * @param isDismissable - show/hide the dismiss (X) icon button. default true (show)
 * @param hasNavContainer - should the banner have padding that matches Nav. default false
 * @param hasStartIcon - should the banner have a left icon. default true
 * @param icon - optional icon override
 * @param onClose - function to call when the banner is dismissed
 * @param variant - styled variants of the banner. default is 'info'
 * @param transition - transition effect for the banner. default is 'fade'
 * @param transitionDuration - duration of the transition effect in seconds. default is 0.3
 * @param autoHideDuration - duration to auto hide the banner in milliseconds.
 * @param verticalOffset - vertical offset for the banner
 * @param horizontalOffset - horizontal offset for the banner
 * @param position - position of the banner. default is 'top-center'
 * @param isOpen - show/hide the banner. default true
 * @param className - additional classnames of the banner
 * @param isSticky - Banner has sticky behavior. default false
 *
 */
export const Banner: React.FC<BannerProps> = ({
  body,
  children,
  dismissLabel,
  isDismissable = true,
  hasNavContainer = false,
  hasStartIcon = true,
  icon,
  onClose,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  variant = 'info',
  transitionName = 'fade',
  transitionDuration = 0.3,
  autoHideDuration,
  verticalOffset,
  horizontalOffset,
  position = 'top-center',
  isOpen = true,
  className,
  isSticky = false,
  ...rest
}) => {
  const [isOpenControlled, setIsOpenControlled] = useState(isOpen);
  const iconName =
    variant === 'info'
      ? 'info'
      : variant === 'critical'
        ? 'circle-close-outlined'
        : 'checkmark-circle';

  const fontColor = variant === 'info' ? 'info' : 'foreverwhite';

  const handleClose = useCallback(
    (reason: closeReason) => (e: React.SyntheticEvent | Event) => {
      setIsOpenControlled(false);
      if (onClose) {
        onClose(e, reason);
      }
    },
    [onClose],
  );

  const handleFocus = (e: React.FocusEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };

  // if autoHideDuration is set, close the banner after the duration
  useEffect(() => {
    if (autoHideDuration) {
      const timer = setTimeout(() => {
        setIsOpenControlled(false);
        handleClose('timeout')(new Event('timeout'));
      }, autoHideDuration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [autoHideDuration, handleClose]);

  // if press Escape key, close the banner
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose('escapeKeyDown')(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClose]);

  // motion transition config
  const motionTransitionConfig = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    slide: {
      initial: { opacity: 0, y: position.includes('top') ? -60 : 60 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: position.includes('top') ? -60 : 60 },
    },
    grow: {
      initial: { opacity: 0, scale: 0 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0 },
    },
    none: {},
  };

  return (
    <AnimatePresence>
      {isOpenControlled && (
        <motion.div
          {...motionTransitionConfig[transitionName]}
          transition={{ duration: transitionDuration, ease: 'easeIn' }}
          className={classNames(
            'vds-banner',
            `vds-banner--${variant}`,
            hasNavContainer && 'vds-banner--nav-container-padding',
            transitionName && `vds-banner--transition-${transitionName}`,
            verticalOffset && `vds-banner--vertical-offset-${verticalOffset}`,
            horizontalOffset && `vds-banner--horizontal-offset-${horizontalOffset}`,
            position && `vds-banner--position-${position}`,
            className,
            isSticky && 'vds-banner--sticky',
          )}
          role="status"
          aria-live="polite"
          aria-labelledby="banner-content"
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...rest}>
          <div id="banner-content" className="vds-banner__left">
            {hasStartIcon && (
              <Icon className="vds-banner__icon" name={icon || iconName} size="medium" />
            )}
            <Body variant="body-01" size="small" weight="regular" color={fontColor}>
              {body}
            </Body>
            {children}
          </div>
          {isDismissable && (
            <button
              className="vds-banner__close-button"
              aria-label={dismissLabel}
              onClick={handleClose('clickClose')}>
              <Icon name="close-outlined" size="medium" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
